import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import BookingForm from "../components/BookingForm";
import { createSlug } from "../slugify";
import { Helmet } from "react-helmet";

const accentColor = "rgb(37, 150, 190)";

// Utility: Render star rating (full stars only)
const renderStars = (count = 5) => (
  <div style={{ marginBottom: 8 }} aria-label={`${count} star rating`}>
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        style={{ color: "#ffb400", fontSize: "1.3rem" }}
        aria-hidden="true"
      >
        ★
      </span>
    ))}
  </div>
);

const packageData = [
  {
    name: "Basic Refresh",
    price: 69.99,
    wasPrice: 99.99,
    features: "Standard steam cleaning & quick drying",
    icon: "💧",
    rating: 4,
    bg: "linear-gradient(135deg, #e0f7fa 0%, #f9faff 100%)",
  },
  {
    name: "Premium Care",
    price: 99.99,
    wasPrice: 149.99,
    features: "Deep cleaning, stain removal & deodorizing",
    icon: "✨",
    rating: 5,
    bg: "linear-gradient(135deg, #b2ebf2 0%, #f9faff 100%)",
  },
  {
    name: "Deluxe Protection",
    price: 149.99,
    wasPrice: 199.99,
    features:
      "All Premium features + fabric protection & eco-friendly products",
    icon: "🛡️",
    rating: 5,
    bg: "linear-gradient(135deg, #80deea 0%, #f9faff 100%)",
  },
];

const featureList = [
  {
    icon: "🧼",
    title: "Deep Steam Cleaning",
    desc: "Our powerful steam extraction penetrates deep to remove stubborn dirt, allergens, and dust mites, ensuring a thorough cleanse.",
  },
  {
    icon: "🌱",
    title: "Eco-Friendly & Safe",
    desc: "We care for your family and the planet by using biodegradable, non-toxic cleaning solutions that are gentle yet effective.",
  },
  {
    icon: "🧽",
    title: "Advanced Stain Removal",
    desc: "Say goodbye to tough stains like wine, coffee, pet accidents, and more with our specialized treatment.",
  },
  {
    icon: "💨",
    title: "Rapid Drying Technology",
    desc: "Our state-of-the-art equipment reduces drying time significantly, so you can enjoy your refreshed carpets sooner.",
  },
  {
    icon: "🌸",
    title: "Odor Neutralization",
    desc: "We eliminate unpleasant odors at the source, leaving your space smelling fresh and inviting.",
  },
];

const stepList = [
  "Comprehensive Inspection: We evaluate your carpet’s condition to tailor the perfect cleaning strategy.",
  "Pre-Treatment: Application of eco-friendly solutions to loosen embedded dirt and stains.",
  "Deep Steam Extraction: Hot water extraction removes dirt, allergens, and bacteria deep within the fibers.",
  "Targeted Spot Treatment: Special attention to stubborn stains and high-traffic areas.",
  "Rapid Drying: Powerful extraction minimizes moisture, speeding up drying time.",
  "Final Quality Check: We ensure your complete satisfaction with a spotless, refreshed carpet.",
];

const ServicePage = ({ services }) => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!services || services.length === 0) {
      setError("No services available.");
      return;
    }
    const matchedService = services.find((svc) => {
      const generatedSlug = createSlug(svc.name).toLowerCase();
      const normalizedSlug = slug.toLowerCase().trim();
      return generatedSlug === normalizedSlug;
    });
    if (!matchedService) {
      setService(null);
      setError("Service not found");
      return;
    }
    setService(matchedService);
  }, [slug, services]);

  if (error)
    return (
      <main
        style={{
          padding: 24,
          maxWidth: 900,
          margin: "auto",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <p style={{ color: "red" }}>{error}</p>
      </main>
    );

  if (!service)
    return (
      <main
        style={{
          padding: 24,
          maxWidth: 900,
          margin: "auto",
          fontFamily: "Arial, sans-serif",
        }}
      >
        <p>Service not found</p>
      </main>
    );

  // Sanitize description text for meta description (strip HTML tags)
  const metaDescription = service.description
    ? service.description.replace(/(<([^>]+)>)/gi, "").slice(0, 160)
    : `Learn more about our ${service.name} carpet cleaning service.`;

  return (
    <>
      <Helmet>
        <title>{service.name} - Instant Carpet Cleaning Services</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={`https://instantcarpetcleaningservices.com.au/services/${slug}`}
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={`${service.name} - Instant Carpet Cleaning`}
        />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:url"
          content={`https://instantcarpetcleaningservices.com.au/services/${slug}`}
        />
        <meta property="og:type" content="website" />
        {service.image && <meta property="og:image" content={service.image} />}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${service.name} - Instant Carpet Cleaning`}
        />
        <meta name="twitter:description" content={metaDescription} />
        {service.image && <meta name="twitter:image" content={service.image} />}

        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.name,
            description: metaDescription,
            provider: {
              "@type": "LocalBusiness",
              name: "Instant Carpet Cleaning Services",
              url: "https://instantcarpetcleaningservices.com.au",
              telephone: "+61411331731",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Sydney",
                addressRegion: "NSW",
                addressCountry: "AU",
              },
            },
            areaServed: {
              "@type": "City",
              name: "Sydney",
            },
            image:
              service.image ||
              "https://instantcarpetcleaningservices.com.au/welcomeimageinstantcleaners.jpg",
          })}
        </script>
      </Helmet>

      <main
        style={{
          padding: 24,
          maxWidth: 900,
          margin: "auto",
          fontFamily: "Arial, sans-serif",
          color: "#333",
          background: "linear-gradient(120deg, #f9faff 60%, #e0f7fa 100%)",
          borderRadius: 16,
          boxShadow: "0 8px 32px rgba(37, 150, 190, 0.10)",
        }}
      >
        <header>
          <h1
            style={{
              fontSize: "2.7rem",
              marginBottom: 12,
              color: accentColor,
              fontWeight: "800",
              textAlign: "center",
              letterSpacing: 1,
            }}
          >
            {service.name}
          </h1>
          <div
            style={{
              width: 80,
              height: 4,
              background: accentColor,
              margin: "0 auto 32px auto",
              borderRadius: 2,
            }}
            aria-hidden="true"
          />
        </header>

        {service.image && (
          <img
            src={service.image}
            alt={service.name}
            style={{
              maxWidth: "100%",
              height: "auto",
              marginBottom: 32,
              borderRadius: 14,
              boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
            loading="lazy"
          />
        )}

        <article
          style={{
            lineHeight: 1.7,
            fontSize: "1.15rem",
            color: "#444",
            marginBottom: 32,
          }}
          aria-label={`${service.name} description`}
          dangerouslySetInnerHTML={{ __html: service.description }}
        />

        <section aria-labelledby="trust-us-heading">
          <h2
            id="trust-us-heading"
            style={{
              color: accentColor,
              fontWeight: 700,
              fontSize: "1.5rem",
              marginBottom: 18,
              marginTop: 10,
            }}
          >
            Why Trust Us With Your Carpets?
          </h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 32,
              marginBottom: 36,
              justifyContent: "center",
            }}
          >
            {featureList.map((f, i) => (
              <div
                key={i}
                style={{
                  flex: "1 1 240px",
                  background: "#fff",
                  borderRadius: 14,
                  boxShadow: "0 2px 12px rgba(37,150,190,0.07)",
                  padding: "28px 22px",
                  minWidth: 180,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 16,
                }}
              >
                <span
                  style={{ fontSize: "1.6rem", marginTop: 2 }}
                  aria-hidden="true"
                >
                  {f.icon}
                </span>
                <div>
                  <b style={{ color: accentColor }}>{f.title}</b>
                  <div style={{ fontSize: "1rem", marginTop: 6 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <p style={{ fontSize: "1.1rem", marginBottom: 30 }}>
          Our mission is to create a healthier indoor environment by removing
          allergens and bacteria that can affect your well-being – all while
          preserving the softness and beauty of your carpets.
        </p>

        <section aria-labelledby="packages-heading">
          <h2
            id="packages-heading"
            style={{
              color: accentColor,
              fontWeight: 700,
              fontSize: "1.5rem",
              marginBottom: 20,
              marginTop: 10,
            }}
          >
            Choose the Perfect Package for Your Needs
          </h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 36,
              justifyContent: "center",
              marginBottom: 44,
              padding: "0 12px",
            }}
          >
            {packageData.map((pkg, i) => (
              <article
                key={i}
                style={{
                  flex: "1 1 240px",
                  minWidth: 220,
                  maxWidth: 300,
                  background: pkg.bg,
                  borderRadius: 18,
                  boxShadow: "0 2px 16px rgba(37,150,190,0.10)",
                  padding: "36px 22px 28px 22px",
                  textAlign: "center",
                  border: `2px solid ${accentColor}20`,
                  margin: "0 8px",
                  transition: "transform 0.2s",
                }}
              >
                <div
                  style={{ fontSize: "2.3rem", marginBottom: 12 }}
                  aria-hidden="true"
                >
                  {pkg.icon}
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: accentColor,
                    marginBottom: 10,
                  }}
                >
                  {pkg.name}
                </div>

                {/* Star Rating */}
                {renderStars(pkg.rating)}

                <div
                  style={{
                    fontSize: "0.95rem",
                    color: "#666",
                    marginBottom: 4,
                    letterSpacing: 0.5,
                    fontWeight: 500,
                  }}
                >
                  Starts from
                </div>

                <div style={{ marginBottom: 8 }}>
                  {pkg.wasPrice && (
                    <span
                      style={{
                        textDecoration: "line-through",
                        color: "#b0b0b0",
                        fontSize: "1.1rem",
                        marginRight: 8,
                        fontWeight: 500,
                      }}
                    >
                      ${pkg.wasPrice}
                    </span>
                  )}
                  <span
                    style={{
                      fontSize: "2rem",
                      fontWeight: 800,
                      color: "#222",
                      letterSpacing: 1,
                    }}
                  >
                    ${pkg.price}
                  </span>
                </div>

                <div style={{ fontSize: "1.07rem", color: "#444" }}>
                  {pkg.features}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section aria-labelledby="process-heading">
          <h2
            id="process-heading"
            style={{
              color: accentColor,
              fontWeight: 700,
              fontSize: "1.5rem",
              marginBottom: 22,
              marginTop: 10,
            }}
          >
            Our Proven 6-Step Cleaning Process
          </h2>
          <ol style={{ paddingLeft: 22, marginBottom: 36 }}>
            {stepList.map((step, idx) => (
              <li key={idx} style={{ marginBottom: 15, fontSize: "1.12rem" }}>
                <b style={{ color: accentColor }}>{step.split(":")[0]}:</b>
                {step.includes(":") ? (
                  <span> {step.split(":").slice(1).join(":")}</span>
                ) : null}
              </li>
            ))}
          </ol>
        </section>

        <p
          style={{
            fontStyle: "italic",
            color: "#555",
            marginBottom: 28,
            fontSize: "1.08rem",
          }}
        >
          Experience the joy of walking on soft, clean carpets that enhance the
          comfort and beauty of your home or office.
        </p>

        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <span
            style={{
              fontWeight: 700,
              color: accentColor,
              fontSize: "1.1rem",
              marginRight: 6,
            }}
          >
            Don’t wait!
          </span>
          <span>
            Contact us today to schedule your appointment and enjoy a healthier,
            fresher living space with our trusted carpet cleaning experts.
          </span>
        </div>

        {/* Booking form with fixedService prop */}
        <section
          aria-label="Booking form"
          style={{
            marginTop: 30,
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 2px 10px rgba(37,150,190,0.06)",
            padding: 28,
            maxWidth: 600,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <BookingForm fixedService={service} />
        </section>

        <nav
          aria-label="Back to home page"
          style={{ textAlign: "center", marginTop: 38 }}
        >
          <Link
            to="/"
            style={{
              color: accentColor,
              textDecoration: "none",
              fontWeight: "bold",
              fontSize: "1.1rem",
              padding: "10px 28px",
              borderRadius: 6,
              background: "linear-gradient(90deg, #e0f7fa 0%, #b2ebf2 100%)",
              boxShadow: "0 2px 6px rgba(37,150,190,0.07)",
              border: `1px solid ${accentColor}40`,
              transition: "background 0.3s, color 0.3s",
              display: "inline-block",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.target.style.background = "#b2ebf2")}
            onMouseLeave={(e) =>
              (e.target.style.background =
                "linear-gradient(90deg, #e0f7fa 0%, #b2ebf2 100%)")
            }
          >
            ← Back to Home
          </Link>
        </nav>
      </main>
    </>
  );
};

export default ServicePage;
